const BASE = '/api/admin/coverage';

async function authHeaders({ json = false } = {}) {
  const { supabase } = await import('./supabaseClient');
  const { data: { session } } = await supabase.auth.getSession();
  const headers = json ? { 'Content-Type': 'application/json' } : {};
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

function queryString(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) params.set(key, value);
  });
  const value = params.toString();
  return value ? `?${value}` : '';
}

export function isAsciiAuditReason(value) {
  return typeof value === 'string' && value.length > 0 && !/[^\x20-\x7E]/.test(value);
}

async function callCoverage(method, path, body, audit) {
  const payload = body === undefined ? undefined : {
    ...body,
    ...(audit?.requestId ? { request_id: audit.requestId } : {}),
    ...(audit?.reason ? { audit_reason: audit.reason } : {}),
  };
  const headers = await authHeaders({ json: payload !== undefined });
  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: payload === undefined ? undefined : JSON.stringify(payload),
    });
  } catch {
    return { error: 'ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی و دوباره تلاش کنید.', code: 'NETWORK_ERROR' };
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    return {
      error: data?.error || `درخواست با خطای ${response.status} انجام نشد.`,
      code: data?.code || 'COVERAGE_ADMIN_FAILED',
      details: data?.details,
      evidence: data?.evidence,
      batch_id: data?.batch_id,
      status: data?.status,
      httpStatus: response.status,
    };
  }
  return data || {};
}

export const listCoverageRegions = () => callCoverage('GET', '/regions');
export const createCoverageRegion = (body, audit) => callCoverage('POST', '/regions', body, audit);
export const listCoverageCarriers = () => callCoverage('GET', '/carriers');
export const listCoverageServices = (filters) => callCoverage('GET', `/services${queryString(filters)}`);
export const listCoverageSources = (filters) => callCoverage('GET', `/sources${queryString(filters)}`);
export const createCoverageSource = (body, audit) => callCoverage('POST', '/sources', body, audit);
export const updateCoverageSource = (id, body, audit) => callCoverage('PATCH', `/sources/${id}`, body, audit);
export const listCoverageImports = (filters) => callCoverage('GET', `/imports${queryString(filters)}`);
export const getCoverageImport = (id) => callCoverage('GET', `/imports/${id}`);
export const getCoverageImportReport = (id) => callCoverage('GET', `/imports/${id}/report`);
export const listCoverageConflicts = (filters) => callCoverage('GET', `/conflicts${queryString(filters)}`);
export const getCoverageConflict = (id) => callCoverage('GET', `/conflicts/${id}`);
export const acknowledgeCoverageConflict = (id, note, audit) => (
  callCoverage('POST', `/conflicts/${id}/acknowledge`, { note }, audit)
);
export const resolveCoverageConflict = (id, note, audit) => (
  callCoverage('POST', `/conflicts/${id}/resolve`, { note }, audit)
);
export const listCoveragePublishRequests = (filters) => (
  callCoverage('GET', `/publish-requests${queryString(filters)}`)
);
export const createCoveragePublishRequest = (body, audit) => (
  callCoverage('POST', '/publish-requests', body, audit)
);
export const approveCoveragePublishRequest = (id, audit) => (
  callCoverage('POST', `/publish-requests/${id}/approve`, {}, audit)
);
export const rejectCoveragePublishRequest = (id, audit) => (
  callCoverage('POST', `/publish-requests/${id}/reject`, {}, audit)
);
export const listCoverageVersions = (filters) => callCoverage('GET', `/versions${queryString(filters)}`);
export const getCoverageVersion = (id) => callCoverage('GET', `/versions/${id}`);
export const listCoveragePolicies = (filters) => callCoverage('GET', `/policies${queryString(filters)}`);

export async function uploadManualCoverage({ sourceId, file, requestId, reason, onProgress }) {
  if (!isAsciiAuditReason(reason)) {
    return {
      code: 'AUDIT_REASON_ASCII_REQUIRED',
      error: 'دلیل حسابرسی بارگذاری باید فقط به انگلیسی و با نویسه‌های ASCII نوشته شود؛ متن فارسی را به انگلیسی بازنویسی کنید.',
    };
  }
  const headers = await authHeaders();
  headers['X-Request-Id'] = requestId;
  headers['X-Audit-Reason'] = reason;
  const form = new FormData();
  form.append('source_id', sourceId);
  form.append('file', file);

  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${BASE}/imports/manual`);
    Object.entries(headers).forEach(([name, value]) => request.setRequestHeader(name, value));
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onerror = () => resolve({
      error: 'بارگذاری به سرور نرسید. اتصال شبکه را بررسی و دوباره تلاش کنید.',
      code: 'NETWORK_ERROR',
    });
    request.onload = () => {
      let data = null;
      try { data = request.responseText ? JSON.parse(request.responseText) : {}; } catch { data = {}; }
      if (request.status < 200 || request.status >= 300) {
        resolve({
          error: data.error || `بارگذاری با خطای ${request.status} متوقف شد.`,
          code: data.code || 'UPLOAD_FAILED',
          evidence: data.evidence,
          batch_id: data.batch_id,
          status: data.status,
          httpStatus: request.status,
        });
        return;
      }
      resolve(data || {});
    };
    request.send(form);
  });
}

const REASON_FA = {
  WRONG_SRID: 'سامانهٔ مختصات فایل ۴۳۲۶ نیست؛ فایل را با WGS84 / EPSG:4326 خروجی بگیرید.',
  WRONG_GEOMETRY_TYPE: 'نوع هندسه پلی‌گون یا مولتی‌پلی‌گون نیست؛ فقط محدوده‌های سطحی قابل پذیرش‌اند.',
  EMPTY_GEOMETRY: 'هندسه خالی است؛ برای این عارضه یک محدودهٔ بسته ثبت کنید.',
  ZERO_AREA: 'مساحت عارضه صفر است؛ ترتیب یا مختصات حلقه را اصلاح کنید.',
  FEATURE_AREA_TOO_SMALL: 'مساحت عارضه از حداقل مجاز کمتر است؛ پلی‌گون بسیار کوچک یا خراب است.',
  FEATURE_AREA_TOO_LARGE: 'مساحت عارضه از سقف مجاز بیشتر است؛ محدوده را بررسی یا تقسیم کنید.',
  OUTSIDE_COMMERCIAL_REGION: 'عارضه از مرز تجاری منطقه بیرون زده است؛ محدودهٔ فایل یا منطقه را اصلاح کنید.',
  RING_TOO_SMALL: 'حلقهٔ پلی‌گون کمتر از سه نقطهٔ متمایز دارد؛ نقاط مرزی را کامل کنید.',
  MISSING_OUTER_RING: 'پلی‌گون حلقهٔ بیرونی ندارد؛ outerBoundaryIs را به فایل اضافه کنید.',
  NON_FINITE_COORDINATE: 'یک مختصات عدد معتبر نیست؛ مقدار خالی، NaN یا بی‌نهایت را در فایل اصلاح کنید.',
  COORDINATE_OUT_OF_RANGE: 'مختصات از بازهٔ معتبر WGS84 بیرون است؛ طول و عرض جغرافیایی را بررسی کنید.',
  OUTSIDE_ALLOWED_BBOX: 'مختصات از محدودهٔ مجاز این منبع بیرون است؛ محدودهٔ منبع یا فایل را بررسی کنید.',
  FEATURE_EXTENT_LIMIT: 'گسترهٔ عارضه بیش از حد مجاز است؛ احتمال جابه‌جایی محور یا مختصات اشتباه را بررسی کنید.',
  UNSUPPORTED_GEOMETRY: 'فایل هندسهٔ پشتیبانی‌نشده دارد؛ فقط Polygon و MultiGeometry شامل Polygon پذیرفته می‌شود.',
  INVALID_GEOMETRY: 'هندسه معتبر نیست؛ خودتقاطع، حلقهٔ باز یا ترتیب نقاط را اصلاح کنید.',
  INVALID_SELF_INTERSECTION: 'هندسه خودتقاطع دارد؛ محل تقاطع حلقه‌ها را در فایل مبدأ اصلاح کنید.',
  INVALID_HOLE_OUTSIDE_SHELL: 'یک حفره بیرون از مرز اصلی پلی‌گون قرار دارد؛ حلقهٔ داخلی را داخل محدوده نگه دارید.',
  INVALID_NESTED_SHELL: 'پلی‌گون پوسته‌های تودرتوی نامعتبر دارد؛ ساختار حلقه‌ها را در فایل مبدأ اصلاح کنید.',
  MALFORMED_COORDINATE: 'ساختار یک مختصات KML نادرست است؛ هر نقطه باید حداقل «طول,عرض» داشته باشد.',
  COORDINATE_LIMIT: 'تعداد مختصات از سقف مجاز بیشتر است؛ فایل را ساده یا به چند فایل تقسیم کنید.',
  RING_COORDINATE_LIMIT: 'یک حلقه بیش از حد نقطه دارد؛ هندسه را ساده‌سازی کنید.',
  KML_MALFORMED: 'ساختار XML/KML خراب است؛ فایل را در نرم‌افزار مبدأ دوباره خروجی بگیرید.',
  KML_EXTERNAL_REFERENCE: 'فایل ارجاع خارجی، NetworkLink یا موجودیت ممنوع دارد؛ داده را داخل فایل نگه دارید.',
  KML_MAX_DEPTH: 'تو در تویی XML از سقف امن بیشتر است؛ ساختار فایل را ساده کنید.',
  FEATURE_LIMIT: 'تعداد عارضه‌ها از سقف مجاز بیشتر است؛ فایل را به چند بارگذاری تقسیم کنید.',
  KML_INVALID_MAGIC: 'محتوا یک سند KML معتبر نیست؛ پسوند و محتوای واقعی فایل را بررسی کنید.',
  UPLOAD_TOO_LARGE: 'حجم درخواست از سقف مجاز بیشتر است؛ فایل باید کمتر از ۲۵ مگابایت باشد.',
  FILE_TOO_LARGE: 'حجم فایل از سقف مجاز منبع بیشتر است؛ فایل را کوچک یا تقسیم کنید.',
  UNSUPPORTED_FILE_TYPE: 'فقط فایل‌های KML و KMZ پذیرفته می‌شوند.',
  DUPLICATE_FILE: 'همین فایل قبلاً با SHA-256 یکسان دریافت شده است؛ گزارش بارگذاری قبلی را باز کنید.',
  DUPLICATE_FEATURE: 'یک عارضهٔ تکراری داخل همین فایل وجود دارد؛ نسخهٔ تکراری را حذف کنید.',
  BATCH_AREA_LIMIT: 'جمع مساحت پذیرفته‌شده از سقف یک بارگذاری بیشتر است؛ محدوده را بررسی یا تقسیم کنید.',
  NO_POLYGONS: 'هیچ Placemark پلی‌گونی پیدا نشد؛ فایل باید حداقل یک محدودهٔ Polygon داشته باشد.',
  MIME_EXTENSION_MISMATCH: 'نوع محتوای فایل با پسوند آن هم‌خوان نیست؛ فایل را با پسوند درست دوباره خروجی بگیرید.',
  ADJACENT_DUPLICATE_COORDINATE_REMOVED: 'مختصات تکراریِ کنار هم حذف شد؛ بهتر است فایل مبدأ پاک‌سازی شود.',
  RING_AUTO_CLOSED: 'حلقهٔ باز به‌صورت خودکار بسته شد؛ بهتر است نقطهٔ اول و آخر در فایل یکسان باشند.',
  VALID_FEATURE_OVERLAP: 'دو محدودهٔ معتبر داخل همین فایل هم‌پوشانی دارند؛ هم‌پوشانی را بازبینی کنید.',
  KMZ_INVALID_MAGIC: 'فایل KMZ امضای ZIP معتبر ندارد؛ آرشیو را دوباره بسازید.',
  KMZ_INVALID_ARCHIVE: 'ساختار آرشیو KMZ خراب یا نامعتبر است؛ از ابزار مبدأ دوباره خروجی بگیرید.',
  KMZ_SIZE_MISMATCH: 'اندازهٔ واقعی یکی از فایل‌های داخل KMZ با مقدار اعلام‌شده فرق دارد؛ آرشیو خراب است.',
  KMZ_CRC_MISMATCH: 'کنترل CRC یکی از ورودی‌های KMZ نامعتبر است؛ آرشیو را دوباره تولید کنید.',
  KMZ_ZIP_SLIP: 'ساختار مسیرهای داخل KMZ ناامن است؛ آرشیو را بدون مسیر مطلق یا ‎..‎ دوباره بسازید.',
  KMZ_UNSAFE_ENTRY_PATH: 'نام یکی از فایل‌های داخل KMZ ناامن است؛ نام ورودی را اصلاح کنید.',
  KMZ_TOO_MANY_ENTRIES: 'تعداد فایل‌های داخل KMZ از سقف مجاز بیشتر است؛ منابع اضافی را حذف کنید.',
  KMZ_ENCRYPTED_ENTRY: 'ورودی رمزگذاری‌شده در KMZ پذیرفته نمی‌شود؛ فایل را بدون رمز خروجی بگیرید.',
  KMZ_UNSUPPORTED_COMPRESSION: 'روش فشرده‌سازی KMZ پشتیبانی نمی‌شود؛ از ZIP استاندارد استفاده کنید.',
  KMZ_UNSAFE_ENTRY_TYPE: 'KMZ شامل symlink یا فایل ویژه است؛ فقط فایل‌های عادی را نگه دارید.',
  KMZ_DUPLICATE_ENTRY: 'نام تکراری داخل KMZ وجود دارد؛ ورودی‌های تکراری را حذف کنید.',
  KMZ_ENTRY_TOO_LARGE: 'یکی از ورودی‌های KMZ بیش از حد بزرگ است؛ منابع اضافی یا تصویرهای سنگین را حذف کنید.',
  KMZ_UNCOMPRESSED_LIMIT: 'حجم بازشدهٔ KMZ از سقف امن بیشتر است؛ آرشیو را کوچک کنید.',
  KMZ_NESTED_ARCHIVE: 'آرشیو تو در تو داخل KMZ ممنوع است؛ فایل فشردهٔ داخلی را حذف کنید.',
  KMZ_MULTIPLE_KML: 'KMZ باید دقیقاً یک سند KML داشته باشد؛ KMLهای اضافی را حذف کنید.',
  KMZ_SUSPICIOUS_COMPRESSION: 'نسبت فشرده‌سازی KMZ غیرعادی و ناامن است؛ آرشیو را دوباره بسازید.',
  KMZ_KML_MISSING: 'داخل KMZ هیچ فایل KML وجود ندارد.',
  MULTIPART_REQUIRED: 'درخواست بارگذاری با قالب multipart ارسال نشده است؛ دوباره فایل را انتخاب کنید.',
  MULTIPART_MALFORMED: 'ساختار درخواست بارگذاری خراب است؛ دوباره تلاش کنید.',
  MULTIPART_FIELDS_INVALID: 'فیلدهای بارگذاری معتبر نیستند؛ منبع را دوباره انتخاب کنید.',
  SOURCE_ID_MUST_PRECEDE_FILE: 'شناسهٔ منبع پیش از فایل ارسال نشده است؛ دوباره بارگذاری کنید.',
  MULTIPART_FILE_INVALID: 'بخش فایل درخواست معتبر نیست؛ فقط یک فایل انتخاب کنید.',
  MULTIPART_PARTS_INVALID: 'درخواست باید فقط شامل یک منبع و یک فایل باشد.',
  SOURCE_ID_REQUIRED: 'پیش از بارگذاری، یک منبع پذیرفته‌شده انتخاب کنید.',
  MANUAL_SOURCE_NOT_AVAILABLE: 'منبع دستی پذیرفته‌شده پیدا نشد؛ ابتدا منبع را بسازید و وضعیت آن را «پذیرفته» بگذارید.',
  UPLOAD_FAILED: 'بارگذاری کامل نشد؛ اتصال و فایل را بررسی و دوباره تلاش کنید.',
  VALIDATION_FAILED: 'اعتبارسنجی داخلی کامل نشد؛ جزئیات فنی گزارش را برای پشتیبانی نگه دارید.',
};

export function coverageReasonFa(reason = {}) {
  const code = reason.code || reason.reason_code || 'UNKNOWN';
  if (REASON_FA[code]) return REASON_FA[code];
  const detail = reason.error || reason.message || reason.reason_detail;
  if (detail) return `${detail} (${code})`;
  return `دلیل ناشناخته از اعتبارسنج: ${code}`;
}
